import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
} from "@/components/atoms/card";
import { Progress } from "@/components/atoms/progress";

interface DashboardCardProps {
  value: number | string;
  min?: number;
  max?: number;
  title: string;
  showIndicator?: boolean;
  monthlyImprovement?: string;
}

export default function DashboardCard({
  value,
  min,
  max,
  title,
  showIndicator = false,
  monthlyImprovement,
}: DashboardCardProps) {
  // Only show indicator if min, max, and value are all defined
  const showBar =
    showIndicator &&
    typeof min === "number" &&
    typeof max === "number" &&
    typeof value === "number";

  // Calculate the fill percentage for the progress bar
  const percentage = showBar
    ? Math.min(100, Math.max(0, ((value - min!) / (max! - min!)) * 100))
    : 0;

  // Determine the color for the indicator dot (not the bar itself)
  let indicatorColor = "bg-green-500";
  if (percentage > 66) {
    indicatorColor = "bg-red-500";
  } else if (percentage > 33) {
    indicatorColor = "bg-yellow-400";
  }

  return (
    <Card className="h-full bg-white w-full min-w-0">
      <CardHeader className="pb-2">
        <CardTitle
          className="text-sm lg:text-xs font-medium text-gray-700 truncate"
          title={title}
        >
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="flex items-center justify-between gap-2 min-w-0">
          <p className="text-lg sm:text-xl lg:text-2xl font-bold truncate min-w-0 flex-1">
            {typeof value === "number" ? value.toLocaleString() : value}
          </p>

          {showBar && (
            <div className="flex flex-col space-y-1 min-w-[80px] sm:min-w-[90px] flex-shrink-0">
              <div className="relative flex items-center">
                <Progress
                  value={percentage}
                  className="w-16 sm:w-20 h-1.5 bg-gray-200"
                />
                {/* Indicator dot */}
                <div
                  className={`absolute top-1/2 -translate-y-1/2 w-2 h-2 rounded-full border border-white ${indicatorColor}`}
                  style={{ left: `calc(${percentage}% - 4px)` }}
                />
              </div>
              <div className="flex justify-between text-[9px] sm:text-[10px] text-gray-500 mt-1">
                <span>&lt;{min}</span>
                <span className="hidden sm:inline">
                  {min}-{max}
                </span>
                <span>&gt;{max}</span>
              </div>
            </div>
          )}
        </div>
        {monthlyImprovement && (
          <p className="text-xs sm:text-sm text-gray-500 mt-1 truncate">
            <span
              className={`text-xs font-medium ${
                parseInt(monthlyImprovement.replace("%", "")) > 0
                  ? "text-green-500"
                  : "text-red-500"
              }`}
            >
              {monthlyImprovement}
            </span>{" "}
            este mes
          </p>
        )}
      </CardContent>
    </Card>
  );
}
